import { Chip, Grid } from '@mui/material';
import { parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import React, { ReactElement } from 'react';

import CommentsIcon from '../../../../../assets/svg/CommentsIcon';
import CustomFieldIcon from '../../../../../assets/svg/CustomFieldsIcon';
import ImageIcon from '../../../../../assets/svg/ImageIcon';
import RequirementsIcon from '../../../../../assets/svg/RequirementsIcon';
import SignatureIcon from '../../../../../assets/svg/SignatureIcon';
import TimeIcon from '../../../../../assets/svg/TimeIcon';
import VolumeIcon from '../../../../../assets/svg/VolumeIcon';
import WeightIcon from '../../../../../assets/svg/WeightIcon';
import { DriversProject } from '../../../../../domain/driversProject/DriversProject';
import { StopReport } from '../../../../../domain/report/StopReport';
import { Stop } from '../../../../../domain/stop/Stop';
import { Text } from '../../../../../react-components/Text';

interface StopReportIconsChipProps {
  report?: StopReport;
  color?: string;
  containerStyle?: React.CSSProperties;
}

export const StopReportIconsChip = ({ report, color, containerStyle = {} }: StopReportIconsChipProps) => {
  if (!report) return null;
  if (!report.comments && !report.images && !report.custom_fields && !report.signature) return null;
  return (
    <Grid
      display={`flex`}
      flexWrap={`wrap`} // perque?
      style={{
        ...containerStyle,
        backgroundColor: color,
        borderRadius: `5px`,
        padding: `5px 3px 5px 8px`,
        width: `fit-content`,
      }}
    >
      {report.comments && (
        <CommentsIcon style={{ height: `0.9rem`, width: `0.9rem`, paddingRight: `5px`, color: RoutalPalette.white }} />
      )}
      {report.images && (
        <ImageIcon style={{ height: `0.9rem`, width: `0.9rem`, paddingRight: `5px`, color: RoutalPalette.white }} />
      )}
      {report.signature && (
        <SignatureIcon style={{ height: `0.9rem`, width: `0.9rem`, paddingRight: `5px`, color: RoutalPalette.white }} />
      )}
      {report.custom_fields && (
        <CustomFieldIcon
          style={{ height: `0.9rem`, width: `0.9rem`, paddingRight: `5px`, color: RoutalPalette.white }}
        />
      )}
    </Grid>
  );
};

interface StopsChipsProps {
  stop: Stop;
  project: DriversProject;
  report?: StopReport;
  color?: string;
}

export const getStopChips = ({ stop, project, report, color }: StopsChipsProps): ReactElement[] => {
  const chips: ReactElement[] = [];

  const styleImg = { height: `0.9rem`, width: `0.9rem`, color: RoutalPalette.white };

  const styleChips = {
    margin: `1px`,
    borderRadius: `5px`,
    backgroundColor: RoutalPalette.secondary40,
    padding: `1px`,
  };

  const styleText = { color: RoutalPalette.white, fontSize: `12px` };

  // Provide requires
  if (stop.stop_fields && stop.stop_fields.some((field) => field.id === `requires`)) {
    const requiresField = stop.stop_fields.find((field) => field.id === `requires`);
    if (requiresField && requiresField.value) {
      (Array.isArray(requiresField.value) ? requiresField.value : []).map((requirement: string) =>
        chips.push(
          <Chip
            style={styleChips}
            key={requirement}
            size="small"
            icon={<RequirementsIcon alt="Time" style={styleImg} />}
            label={
              <Text style={styleText} variant="body">
                {requirement}
              </Text>
            }
          />
        )
      );
    }

    //Time window
    if (stop.stop_fields && stop.stop_fields.some((field) => field.id === `time_window`)) {
      const timeWindowField = stop.stop_fields.find((field) => field.id === `time_window`);
      if (timeWindowField && timeWindowField.value) {
        (Array.isArray(timeWindowField.value) ? timeWindowField.value : []).map((timeWindow: TimeWindow) =>
          chips.push(
            <Chip
              key={timeWindow[0] + timeWindow[1]}
              style={styleChips}
              size="small"
              icon={<TimeIcon alt="Time" style={styleImg} />}
              label={
                <Text style={styleText} variant="body">{`${parseSecondsToHM(timeWindow[0])} - ${parseSecondsToHM(
                  timeWindow[1]
                )}`}</Text>
              }
            />
          )
        );
      }
    }
    //Weight
    if (stop.stop_fields && stop.stop_fields.some((field) => field.id === `weight`)) {
      const weightField = stop.stop_fields.find((field) => field.id === `weight`);
      if (weightField && weightField.value) {
        chips.push(
          <Chip
            style={styleChips}
            size="small"
            icon={<WeightIcon alt="weight" style={styleImg} />}
            label={<Text style={styleText} variant="body">{`${weightField.value} ${project.units.weight}`}</Text>}
          />
        );
      }
    }

    //Volume
    if (stop.stop_fields && stop.stop_fields.some((field) => field.id === `volume`)) {
      const volumeField = stop.stop_fields.find((field) => field.id === `volume`);
      if (volumeField && volumeField.value) {
        chips.push(
          <Chip
            style={styleChips}
            size="small"
            icon={<VolumeIcon alt="volume" style={styleImg} />}
            label={<Text style={styleText} variant="body">{`${volumeField.value} ${project.units.volume}`}</Text>}
          />
        );
      }
    }

    chips.push(<StopReportIconsChip report={report} color={color} containerStyle={styleChips} />);
  }

  return chips;
};
